import { TagMatch } from '../types';

interface PendingMatch {
  id: string;
  productId: string;
  tagNumber: string;
  quantity: number;
  matchedBy: string;
  matchedAt: string;
  timestamp: number;
}

class OfflineManager {
  private dbName = 'rfid-match-offline';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store for pending matches
        if (!db.objectStoreNames.contains('pendingMatches')) {
          const store = db.createObjectStore('pendingMatches', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async savePendingMatch(match: Omit<PendingMatch, 'id' | 'timestamp'>): Promise<string> {
    await this.initDB();
    
    const pendingMatch: PendingMatch = {
      ...match,
      id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(['pendingMatches'], 'readwrite');
      const store = transaction.objectStore('pendingMatches');
      const request = store.add(pendingMatch);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(pendingMatch.id);
    });
  }

  async getPendingMatches(): Promise<PendingMatch[]> {
    await this.initDB();
    
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(['pendingMatches'], 'readonly');
      const store = transaction.objectStore('pendingMatches');
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async clearPendingMatch(id: string): Promise<void> {
    await this.initDB();
    
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(['pendingMatches'], 'readwrite');
      const store = transaction.objectStore('pendingMatches');
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clearAllPendingMatches(): Promise<void> {
    await this.initDB();
    
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(['pendingMatches'], 'readwrite');
      const store = transaction.objectStore('pendingMatches');
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  async registerBackgroundSync(): Promise<void> {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('tag-match-sync');
    }
  }

  async syncPendingMatches(): Promise<void> {
    const pendingMatches = await this.getPendingMatches();
    
    if (pendingMatches.length === 0) {
      return;
    }

    // In a real implementation, this would send to API
    // For now, we'll simulate successful sync
    console.log('Syncing pending matches:', pendingMatches);
    
    // Clear all pending matches after successful sync
    await this.clearAllPendingMatches();
    
    // Dispatch event to notify components
    window.dispatchEvent(new CustomEvent('offline-sync-complete', {
      detail: { syncedMatches: pendingMatches.length }
    }));
  }

  startPeriodicSync(): void {
    // Try to sync every 30 seconds when online
    setInterval(async () => {
      if (this.isOnline()) {
        try {
          await this.syncPendingMatches();
        } catch (error) {
          console.error('Periodic sync failed:', error);
        }
      }
    }, 30000);
  }

  // Listen for online/offline events
  setupNetworkListeners(): void {
    window.addEventListener('online', async () => {
      console.log('Back online - syncing pending matches');
      try {
        await this.syncPendingMatches();
      } catch (error) {
        console.error('Sync on reconnect failed:', error);
      }
    });

    window.addEventListener('offline', () => {
      console.log('Gone offline - matches will be queued');
    });
  }
}

export const offlineManager = new OfflineManager();